import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const JobsContext = createContext(null);

const API = 'https://reportautomation.finkraftai.com/api';

export function JobsProvider({ children }) {
  const [jobs, setJobs] = useState({});   // run_id → job object
  const pollRefs = useRef({});            // run_id → interval handle

  const updateJob = useCallback((run_id, patch) => {
    setJobs(prev => ({ ...prev, [run_id]: { ...(prev[run_id] || {}), ...patch } }));
  }, []);

  const startPolling = useCallback((run_id) => {
    if (pollRefs.current[run_id]) return;
    pollRefs.current[run_id] = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/run/${run_id}`);
        updateJob(run_id, data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollRefs.current[run_id]);
          delete pollRefs.current[run_id];
        }
      } catch (e) {
        console.error('Poll error', run_id, e);
      }
    }, 1500);
  }, [updateJob]);

  // submitJob: called from RunPage after the API call is already made.
  // Accepts (client, file, username, reportType, run_id) — run_id comes from the API response.
  const submitJob = useCallback((client, file, username, reportType, run_id) => {
    if (!run_id) return;
    updateJob(run_id, {
      run_id,
      client,
      report_type: reportType,
      filename: file.name,
      username,
      status: 'processing',
      current_step: 'Queued',
      steps: [],
      started_at: new Date().toISOString(),
    });
    startPolling(run_id);
    return run_id;
  }, [updateJob, startPolling]);

  const dismissJob = useCallback((run_id) => {
    if (pollRefs.current[run_id]) {
      clearInterval(pollRefs.current[run_id]);
      delete pollRefs.current[run_id];
    }
    setJobs(prev => {
      const next = { ...prev };
      delete next[run_id];
      return next;
    });
  }, []);

  const jobList = Object.values(jobs).sort(
    (a, b) => new Date(b.started_at) - new Date(a.started_at)
  );

  return (
    <JobsContext.Provider value={{ jobs, jobList, submitJob, updateJob, dismissJob, API }}>
      {children}
    </JobsContext.Provider>
  );
}

export const useJobs = () => useContext(JobsContext);
